import { MapPin, Navigation, LocateFixed } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useLanguage } from "@/i18n/LanguageContext";
import { useExternalLink } from "@/contexts/ExternalLinkContext";

const RESERVE_URL = "https://www.foodbooking.com/ordering/?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538&reservation=true";
const GOOGLE_MAPS_DIRECTIONS = "https://maps.google.com/?cid=8868003826117860262";

const GeoMessage = () => {
  const { status, distanceKm, requestLocation } = useGeolocation();
  const { t } = useLanguage();
  const { openLink } = useExternalLink();

  // Before user interaction, show a prompt button
  if (status === "idle") {
    return (
      <div className="bg-primary/5 border border-primary/10 px-4 py-3 flex items-center justify-center gap-3 text-center">
        <button
          onClick={requestLocation}
          className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors"
        >
          <LocateFixed className="w-4 h-4 text-aka" />
          <span>{t("geo_find_nearby") ?? "¿Estás cerca? Descúbrelo"}</span>
        </button>
      </div>
    );
  }

  if (status === "loading") return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-primary/10 border border-primary/20 px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 text-center"
      >
        <div className="flex items-center gap-2 text-sm font-body">
          <MapPin className="w-4 h-4 text-aka shrink-0" />
          <span className="text-foreground/80">
            {status === "nearby" ? (
              <>
                {t("geo_nearby_message")}
                {distanceKm !== null && (
                  <span className="text-aka font-semibold ml-1">
                    ({distanceKm} km)
                  </span>
                )}
              </>
            ) : (
              t("geo_far_message")
            )}
          </span>
        </div>
        <div className="flex gap-2">
          {status === "nearby" ? (
            <>
              <button
                onClick={() => openLink(RESERVE_URL)}
                className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-4 py-1.5 hover:bg-primary/90 transition-colors font-body"
              >
                {t("cta_reserve_now")}
              </button>
              <a
                href={GOOGLE_MAPS_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 border border-border text-xs tracking-widest uppercase px-4 py-1.5 hover:bg-accent transition-colors font-body"
              >
                <Navigation className="w-3 h-3" />
                {t("geo_directions")}
              </a>
            </>
          ) : (
            <button
              onClick={() => openLink(RESERVE_URL)}
              className="border border-primary text-primary text-xs tracking-widest uppercase px-4 py-1.5 hover:bg-primary/10 transition-colors font-body"
            >
              {t("cta_view_menu")}
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GeoMessage;
